import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EmpleadoForm from '../components/EmpleadoForm';
import { useAuth } from '../hooks/useAuth';
import empleadoService from '../services/empleadoService';
import afpService from '../services/afpService';

// Componente personalizado del formulario para edición que integra el historial en la sección laboral
const EmpleadoFormEdicion = ({ empleado, onSubmit, onCancel, isLoading, onHistorialLaboralActualizado }) => {
  return (
    <EmpleadoForm
      empleado={empleado}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isLoading={isLoading}
      historialLaboralEdicion={empleado?.historialLaboral || []}
      modoEdicion={true}
      onHistorialLaboralActualizado={onHistorialLaboralActualizado}
    />
  );
};

const EditarEmpleado = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedCompany } = useAuth();
  const [empleado, setEmpleado] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingEmpleado, setLoadingEmpleado] = useState(true);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '', visible: false });
  const [afpsData, setAfpsData] = useState([]);

  const mostrarMensaje = (tipo, texto) => {
    setMensaje({ tipo, texto, visible: true });
    setTimeout(() => setMensaje({ ...mensaje, visible: false }), 5000);
  };

  // Función para mapear datos del backend al formato del formulario
  const mapearEmpleadoParaFormulario = (empleadoData) => {
    const empleadoMapeado = {
      id: empleadoData.id,
      // Información Personal
      nombreCompleto: empleadoData.nombre_completo || '',
      tipoDocumento: empleadoData.tipo_documento || 'DNI',
      numeroDocumento: empleadoData.numero_documento || '',
      fechaNacimiento: empleadoData.fecha_nacimiento || '',
      sexo: empleadoData.sexo || '',
      estadoCivil: empleadoData.estado_civil || '',
      numeroHijos: empleadoData.numero_hijos || 0,
      telefono: empleadoData.telefono || '',
      email: empleadoData.email || '',
      direccion: empleadoData.direccion || '',
      distrito: empleadoData.distrito || '',
      provincia: empleadoData.provincia || '',
      departamento: empleadoData.departamento || '',
      
      // Información Laboral (incluimos estado y fechaIngreso pero no se validan en modo edición)
      cargo: empleadoData.cargo || '',
      categoria: empleadoData.categoria || '',
      areaTrabajo: empleadoData.areas_de_trabajo || '',
      proyecto: empleadoData.proyecto || '',
      tipoContratacion: empleadoData.tipo_contratacion || '',
      regimenLaboral: empleadoData.regimen_laboral?.nombre || '',
      regimenLaboralId: empleadoData.regimen_laboral_id || '',
      computoRhId: empleadoData.fecha_computo_rh_id || '',
      
      // ✅ Incluimos estos campos de la API pero no se muestran/validan en edición
      estado: empleadoData.estado || '',
      fechaIngreso: empleadoData.fecha_ingreso || '',
      
      // Información Salarial
      sueldoBasico: empleadoData.sueldo_basico || '',
      
      // Sistema Pensionario
      sistemaPensionario: empleadoData.sistema_pensionario || 'AFP',
      afp: empleadoData.afp?.nombre || '',
      afpId: empleadoData.afp_id || '',
      cuspp: empleadoData.cuspp || '',
      tipoAfp: empleadoData.tipo_afp || 'Flujo',
      
      // Horarios
      horaEntrada: empleadoData.hora_entrada || '08:00',
      horaSalida: empleadoData.hora_salida || '17:00',
      
      // Información Bancaria
      cuentaBancaria: empleadoData.cuenta_bancaria || '',
      banco: empleadoData.banco || '',
      cci: empleadoData.cci || '',
      cuentaCts: empleadoData.cuenta_cts || '',
      bancoCts: empleadoData.banco_cts || '',
      
      // Contacto de Emergencia
      contactoEmergencia: empleadoData.contacto_emergencia || '',
      telefonoEmergencia: empleadoData.telefono_emergencia || '',
      
      // Observaciones
      observaciones: empleadoData.observaciones || '',
      
      // Configuraciones Adicionales
      asignacionFamiliar: empleadoData.asignacion_familiar || false,
      tieneBono: empleadoData.tiene_bono || false,
      montoBono: empleadoData.monto_bono || '',
      esBoletaLiquidada: empleadoData.es_boleta_liquidada || false,
      
      // Historial Laboral
      historialLaboral: empleadoData.historial_laboral || [],
      
      // Conceptos separados por tipo
      ingresos: empleadoData.conceptos?.filter(c => c.tipo_concepto === 'ingreso').map(c => ({
        id: c.id,
        plame_configuracion_id: c.plame_configuracion_id,
        codigoPlame: c.plame_configuracion?.codigo_plame || '',
        concepto: c.plame_configuracion?.descripcion || '',
        monto: parseFloat(c.monto) || 0,
        tipo_concepto: c.tipo_concepto
      })) || [],
      
      descuentos: empleadoData.conceptos?.filter(c => c.tipo_concepto === 'descuento').map(c => ({
        id: c.id,
        plame_configuracion_id: c.plame_configuracion_id,
        codigoPlame: c.plame_configuracion?.codigo_plame || '',
        concepto: c.plame_configuracion?.descripcion || '',
        monto: parseFloat(c.monto) || 0,
        tipo_concepto: c.tipo_concepto
      })) || [],
      
      aportes: empleadoData.conceptos?.filter(c => c.tipo_concepto === 'aporte').map(c => ({
        id: c.id,
        plame_configuracion_id: c.plame_configuracion_id,
        codigoPlame: c.plame_configuracion?.codigo_plame || '',
        concepto: c.plame_configuracion?.descripcion || '',
        monto: parseFloat(c.monto) || 0,
        tipo_concepto: c.tipo_concepto
      })) || []
    };
    
    // Log para verificar que las configuraciones adicionales se están mapeando correctamente
    console.log('🔍 [EditarEmpleado] Configuraciones adicionales originales:', {
      asignacion_familiar: empleadoData.asignacion_familiar,
      tiene_bono: empleadoData.tiene_bono,
      monto_bono: empleadoData.monto_bono,
      es_boleta_liquidada: empleadoData.es_boleta_liquidada
    });
    console.log('⚙️ [EditarEmpleado] Configuraciones adicionales mapeadas:', {
      asignacionFamiliar: empleadoMapeado.asignacionFamiliar,
      tieneBono: empleadoMapeado.tieneBono,
      montoBono: empleadoMapeado.montoBono,
      esBoletaLiquidada: empleadoMapeado.esBoletaLiquidada
    });
    
    return empleadoMapeado;
  };

  // Función para mapear datos del formulario al formato del backend
  // IMPORTANTE: En modo edición NO incluimos fecha_ingreso ni estado
  // Estos campos solo se pueden modificar al crear el empleado o desde el historial laboral
  const mapearEmpleadoParaBackend = (empleadoData) => {
    return {
      // Información Personal
      nombre_completo: empleadoData.nombreCompleto,
      tipo_documento: empleadoData.tipoDocumento,
      numero_documento: empleadoData.numeroDocumento,
      fecha_nacimiento: empleadoData.fechaNacimiento,
      sexo: empleadoData.sexo,
      estado_civil: empleadoData.estadoCivil,
      numero_hijos: parseInt(empleadoData.numeroHijos) || 0,
      telefono: empleadoData.telefono,
      email: empleadoData.email,
      direccion: empleadoData.direccion,
      distrito: empleadoData.distrito,
      provincia: empleadoData.provincia,
      departamento: empleadoData.departamento,
      
      // Información Laboral (incluimos estado, pero NO fecha_ingreso que no se modifica en edición)
      cargo: empleadoData.cargo,
      categoria: empleadoData.categoria,
      areas_de_trabajo: empleadoData.areaTrabajo,
      proyecto: empleadoData.proyecto,
      tipo_contratacion: empleadoData.tipoContratacion,
      regimen_laboral_id: empleadoData.regimenLaboralId || null,
      fecha_computo_rh_id:
        empleadoData.tipoContratacion === 'Recibo por Honorarios' && empleadoData.computoRhId
          ? parseInt(empleadoData.computoRhId, 10)
          : null,
      estado: empleadoData.estado, // ✅ Incluimos el estado actual
      
      // Información Salarial
      sueldo_basico: parseFloat(empleadoData.sueldoBasico) || 0,
      
      // Sistema Pensionario
      sistema_pensionario: empleadoData.sistemaPensionario,
      afp_id: obtenerIdAfp(empleadoData.afp, empleadoData.sistemaPensionario),
      cuspp: empleadoData.sistemaPensionario === 'AFP' ? empleadoData.cuspp : null,
      tipo_afp: empleadoData.sistemaPensionario === 'AFP' ? empleadoData.tipoAfp : "Flujo",
      
      // Horarios
      hora_entrada: empleadoData.horaEntrada,
      hora_salida: empleadoData.horaSalida,
      
      // Información Bancaria
      cuenta_bancaria: empleadoData.cuentaBancaria,
      banco: empleadoData.banco,
      cci: empleadoData.cci,
      cuenta_cts: empleadoData.cuentaCts,
      banco_cts: empleadoData.bancoCts,
      
      // Contacto de Emergencia
      contacto_emergencia: empleadoData.contactoEmergencia,
      telefono_emergencia: empleadoData.telefonoEmergencia,
      
      // Observaciones
      observaciones: empleadoData.observaciones,
      
      // Configuraciones Adicionales
      asignacion_familiar: Boolean(empleadoData.asignacionFamiliar),
      tiene_bono: Boolean(empleadoData.tieneBono),
      monto_bono: empleadoData.tieneBono ? (parseFloat(empleadoData.montoBono) || 0) : 0,
      es_boleta_liquidada: Boolean(empleadoData.esBoletaLiquidada),
      
      // ID de empresa
      empresa_id: selectedCompany.id,
      
      // Conceptos PLAME (igual que al registrar empleado)
      conceptos: [
        // Ingresos
        ...(empleadoData.ingresos || []).map(ingreso => ({
          plame_configuracion_id: ingreso.plame_configuracion_id || ingreso.id,
          tipo_concepto: 'ingreso',
          monto: parseFloat(ingreso.monto) || 0
        })),
        // Descuentos
        ...(empleadoData.descuentos || []).map(descuento => ({
          plame_configuracion_id: descuento.plame_configuracion_id || descuento.id,
          tipo_concepto: 'descuento',
          monto: parseFloat(descuento.monto) || 0
        })),
        // Aportes
        ...(empleadoData.aportes || []).map(aporte => ({
          plame_configuracion_id: aporte.plame_configuracion_id || aporte.id,
          tipo_concepto: 'aporte',
          monto: parseFloat(aporte.monto) || 0
        }))
      ]
    };
  };

  useEffect(() => {
    cargarEmpleado();
    cargarAfps();
  }, [id]);

  const cargarEmpleado = async () => {
    try {
      setLoadingEmpleado(true);
      
      console.log('🔄 [EditarEmpleado] Iniciando carga de empleado ID:', id);
      const response = await empleadoService.getEmpleadoById(id);
      
      if (response.success && response.data) {
        console.log('📊 [EditarEmpleado] Respuesta completa de la API:', response);
        console.log('📊 [EditarEmpleado] Datos del empleado desde API:', response.data);
        
        // ===== LOGGING ESPECÍFICO PARA CHECKBOXES =====
        console.log('🔍 [EditarEmpleado] DATOS ORIGINALES DE CHECKBOXES DESDE BACKEND:', {
          asignacion_familiar: response.data.asignacion_familiar,
          tipo_asignacion_familiar: typeof response.data.asignacion_familiar,
          tiene_bono: response.data.tiene_bono,
          tipo_tiene_bono: typeof response.data.tiene_bono,
          monto_bono: response.data.monto_bono,
          tipo_monto_bono: typeof response.data.monto_bono,
          es_boleta_liquidada: response.data.es_boleta_liquidada,
          tipo_es_boleta_liquidada: typeof response.data.es_boleta_liquidada
        });
        // ===============================================
        
        const empleadoMapeado = mapearEmpleadoParaFormulario(response.data);
        
        // ===== LOGGING ESPECÍFICO PARA CHECKBOXES MAPEADOS =====
        console.log('⚙️ [EditarEmpleado] DATOS MAPEADOS DE CHECKBOXES PARA FORMULARIO:', {
          asignacionFamiliar: empleadoMapeado.asignacionFamiliar,
          tipo_asignacionFamiliar: typeof empleadoMapeado.asignacionFamiliar,
          tieneBono: empleadoMapeado.tieneBono,
          tipo_tieneBono: typeof empleadoMapeado.tieneBono,
          montoBono: empleadoMapeado.montoBono,
          tipo_montoBono: typeof empleadoMapeado.montoBono,
          esBoletaLiquidada: empleadoMapeado.esBoletaLiquidada,
          tipo_esBoletaLiquidada: typeof empleadoMapeado.esBoletaLiquidada
        });
        // ===================================================
        
        console.log('🔧 [EditarEmpleado] Empleado completamente mapeado:', empleadoMapeado);
        setEmpleado(empleadoMapeado);
      } else {
        mostrarMensaje('error', response.message || 'Error al cargar el empleado');
        setEmpleado(null);
      }

    } catch (error) {
      console.error('❌ [EditarEmpleado] Error al cargar empleado:', error);
      mostrarMensaje('error', error.message || 'Error al cargar los datos del empleado');
      setEmpleado(null);
    } finally {
      setLoadingEmpleado(false);
    }
  };

  const recargarEmpleadoTrasHistorial = async () => {
    if (!id) return;
    const response = await empleadoService.getEmpleadoById(id);
    if (response.success && response.data) {
      setEmpleado(mapearEmpleadoParaFormulario(response.data));
    }
  };

  const cargarAfps = async () => {
    try {
      const response = await afpService.obtenerAfps();
      
      if (response.success && response.data) {
        setAfpsData(response.data);
      }
    } catch (error) {
      console.error('Error al cargar AFPs:', error);
    }
  };

  // Función auxiliar para obtener ID de AFP
  const obtenerIdAfp = (nombreAfp, sistemaPensionario) => {
    // Si el sistema pensionario no es AFP, siempre retornar null
    if (sistemaPensionario !== 'AFP') return null;
    
    // Si no hay nombre de AFP o no hay datos de AFPs, retornar null
    if (!nombreAfp || !afpsData.length) return null;
    
    // Buscar la AFP por nombre
    const afp = afpsData.find(a => a.nombre === nombreAfp || a.nombre === `AFP ${nombreAfp}`);
    return afp?.id || null;
  };

  const handleSubmit = async (empleadoData) => {
    if (!selectedCompany?.id) {
      mostrarMensaje('error', 'Error: Debe seleccionar una empresa para actualizar el empleado');
      return;
    }

    if (!empleado?.id) {
      mostrarMensaje('error', 'Error: No se puede actualizar un empleado sin identificador válido');
      return;
    }

    try {
      setIsLoading(true);
      
      const datosActualizacion = mapearEmpleadoParaBackend(empleadoData);

      const response = await empleadoService.updateEmpleado(empleado.id, datosActualizacion);
      
      if (response.success) {
        mostrarMensaje('success', `✅ Los datos de ${empleadoData.nombreCompleto} han sido actualizados correctamente`);
        
        // Redirigir a la lista de empleados después de 2 segundos
        setTimeout(() => {
          navigate('/empleados');
        }, 2000);
      } else {
        // Mejorar mensajes de error del backend
        let mensajeError = 'Error al actualizar el empleado';
        if (response.message) {
          if (response.message.includes('documento')) {
            mensajeError = 'Ya existe otro empleado con este número de documento';
          } else if (response.message.includes('validation')) {
            mensajeError = 'Los datos ingresados no son válidos. Verifique la información';
          } else {
            mensajeError = response.message;
          }
        }
        mostrarMensaje('error', mensajeError);
      }

    } catch (error) {
      console.error('Error al actualizar empleado:', error);
      
      // Mejorar mensajes de error según el tipo
      let mensajeError = 'Error al actualizar el empleado';
      
      if (error.response?.status === 422) {
        mensajeError = 'Datos inválidos: Verifique que todos los campos estén correctamente completados';
      } else if (error.response?.status === 404) {
        mensajeError = 'El empleado no fue encontrado en el sistema';
      } else if (error.response?.status === 409) {
        mensajeError = 'Conflicto: Ya existe otro empleado con este número de documento';
      } else if (error.response?.status >= 500) {
        mensajeError = 'Error del servidor: Intente nuevamente en unos momentos';
      } else if (error.message) {
        mensajeError = error.message;
      }
      
      mostrarMensaje('error', mensajeError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/empleados');
  };

  if (!selectedCompany) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empresa seleccionada</h3>
          <p className="text-gray-500 text-center">
            Selecciona una empresa para editar empleados.
          </p>
        </div>
      </div>
    );
  }

  if (loadingEmpleado) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Cargando datos del empleado...</span>
        </div>
      </div>
    );
  }

  if (!empleado) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Empleado no encontrado</h3>
          <p className="text-gray-500 text-center mb-4">
            El empleado que buscas no existe o no tienes permisos para verlo.
          </p>
          <button
            onClick={handleCancel}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Volver a empleados
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={handleCancel}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver
          </button>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Editar Empleado</h1>
        <p className="text-gray-600 mt-2">
          Actualizar información de {empleado.nombreCompleto} - {selectedCompany.name}
        </p>
      </div>

      {/* Mensajes */}
      {mensaje.visible && (
        <div className={`mb-6 p-4 rounded-md ${
          mensaje.tipo === 'success' ? 'bg-green-100 text-green-700 border border-green-300' : 
          'bg-red-100 text-red-700 border border-red-300'
        }`}>
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mensaje.tipo === 'success' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              )}
            </svg>
            {mensaje.texto}
          </div>
        </div>
      )}

      {/* Formulario */}
      <div className="bg-gray-50 rounded-lg p-6">
        <EmpleadoForm
          empleado={empleado}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isLoading}
          historialLaboralEdicion={empleado?.historialLaboral || []}
          modoEdicion={true}
          onHistorialLaboralActualizado={recargarEmpleadoTrasHistorial}
        />
      </div>
    </div>
  );
};

export default EditarEmpleado; 